import type { CSSProperties, ReactNode } from 'react';

import type {
  StorefrontCategory,
  StorefrontMenuItem,
} from '@/features/storefront/api/storefront';
import type { StorefrontTheme } from '@/features/storefront/themeSchema';

/**
 * What every theme's header and footer receive.
 *
 * These live apart from the dispatchers that pick a theme's variant: a theme
 * package imports the props, the dispatcher imports the packages, and a shared
 * module in the middle is what keeps that from being a circle.
 */

export interface HeaderProps {
  storeName: string;
  logoUrl: string | null;
  /**
   * The store's own one-line description. Only the themes that have a place
   * for it render it - Serra puts it on its announcement rail - so passing it
   * costs the others nothing.
   */
  description?: string | null;
  header: StorefrontTheme['header'];
  menu?: { name: string; items: StorefrontMenuItem[] };
  /**
   * The account and cart corner, built by the caller. Variants stay free of
   * `useCustomerAuth`/`useCart` on purpose: the customizer preview renders
   * them with no providers around, and passes static stand-ins here instead.
   */
  endSlot: ReactNode;
  preview?: boolean;
}

export interface FooterProps {
  storeName: string;
  /** The merchant's footer appearance; a theme reads it for its copyright line. */
  footer?: StorefrontTheme['footer'];
  description?: string | null;
  /** The store's logo, for the footers that draw one (the shared large footer). */
  logoUrl?: string | null;
  menu?: { name: string; items: StorefrontMenuItem[] };
  /**
   * Inert links, and no fallback page/blog queries: the customizer preview has
   * no storefront host resolved, so those requests would ask about the wrong
   * shop - or no shop - and the merchant's menu is shown as data instead.
   */
  preview?: boolean;
}

/** The nav items a header or footer actually renders, or none. */
export function menuItems(menu: HeaderProps['menu']): StorefrontMenuItem[] {
  return menu && menu.items.length > 0 ? menu.items : [];
}

/**
 * A row that scrolls sideways without drawing a scrollbar under itself.
 *
 * Four of the mastheads carry a nav row on one line and let it scroll when it
 * runs long. On a platform that draws overlay scrollbars that costs nothing;
 * on Windows it puts a full-width grey trough across the shop's navigation,
 * which reads as a broken header rather than as a row with more in it - and it
 * is drawn whether or not the row actually overflows at that width.
 *
 * So the trough goes and the scrolling stays: a pointer still drags the row, a
 * trackpad and a touchscreen still swipe it, and what tells a shopper there is
 * more is the same thing that tells them in every other scrolling row - the
 * link cut off at the edge. Merchants who would rather not have a row that
 * scrolls at all now have `category_menu_limit` to shorten it with.
 */
export const CHROME_QUIET_SCROLL = '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

/**
 * How many departments a masthead's category strip stands in: the merchant's
 * own number when they have chosen one, and the number the theme was drawn
 * with when they have not.
 *
 * Zero is "unset" rather than "show none". A strip of no departments is a
 * header with no way into the catalogue - the very thing the strip exists to
 * stand in for - so it is not a setting worth offering, and the free value is
 * spent on the default instead. That is also what a theme document saved
 * before merchants could choose parses to, so it renders exactly as it did.
 */
export function headerCategoryLimit(
  header: HeaderProps['header'] | undefined,
  themeDefault: number,
): number {
  const chosen = header?.category_menu_limit ?? 0;
  return chosen > 0 ? chosen : themeDefault;
}

/**
 * How many shelves a footer lists, and the most it stacks in one column.
 *
 * A footer has room a masthead does not, so it shows far more of the catalogue
 * than the header strip - but a single list of twelve would run well past the
 * wordmark beside it, so past six they are split across two columns.
 */
export const FOOTER_CATEGORY_LIMIT = 12;
export const FOOTER_CATEGORY_COLUMN = 6;

/**
 * The shelves, cut into the columns they will be laid out in: none, one, or
 * two balanced ones. Balanced rather than filled-then-spilled, so seven
 * categories read as 4 + 3 rather than 6 + 1.
 */
export function categoryColumns(shelves: StorefrontCategory[]): StorefrontCategory[][] {
  if (shelves.length === 0) return [];
  if (shelves.length <= FOOTER_CATEGORY_COLUMN) return [shelves];
  const half = Math.ceil(shelves.length / 2);
  return [shelves.slice(0, half), shelves.slice(half)];
}

/** The most link columns the shared large footer sets beside the shop's own. */
export const LARGE_FOOTER_COLUMNS = 4;

/** One link column of the large footer. */
export interface LinkColumn {
  key: string;
  title: string;
  /** The item the column is headed by, when it is one - its heading links there. */
  head?: StorefrontMenuItem;
  links: StorefrontMenuItem[];
}

/**
 * The footer menu as the large footer's columns: one per item with links
 * under it, in the merchant's order, and one for the items with none, where
 * the first of those stood. At most `LARGE_FOOTER_COLUMNS`.
 */
export function menuColumns(items: StorefrontMenuItem[], menuTitle: string): LinkColumn[] {
  const columns: LinkColumn[] = [];
  let loose: LinkColumn | null = null;
  for (const item of items) {
    const children = item.items ?? [];
    if (children.length > 0) {
      columns.push({ key: `group:${item.label}:${item.url}`, title: item.label, head: item, links: children });
    } else if (loose) {
      loose.links.push(item);
    } else {
      loose = { key: 'menu', title: menuTitle, links: [item] };
      columns.push(loose);
    }
  }
  return columns.slice(0, LARGE_FOOTER_COLUMNS);
}

/**
 * A panel's own colours - a mega panel, the search suggestions: the page's,
 * whatever deck it hangs from. A family may repaint its masthead by
 * redeclaring the storefront's colour names (as Tumble's violet deck does);
 * the `palette-*` names are never redeclared, so they put the theme's real
 * colours back for everything inside the panel.
 */
export const CHROME_PANEL_STYLE = {
  '--storefront-primary': 'var(--storefront-palette-primary, var(--storefront-primary))',
  '--storefront-text': 'var(--storefront-palette-text, var(--storefront-text))',
  '--storefront-muted': 'var(--storefront-palette-muted, var(--storefront-muted))',
  '--storefront-background': 'var(--storefront-palette-background, var(--storefront-background))',
  '--storefront-surface': 'var(--storefront-palette-surface, var(--storefront-surface))',
  backgroundColor: 'var(--storefront-palette-background, var(--storefront-background))',
  borderColor: 'var(--storefront-palette-surface, var(--storefront-surface))',
  color: 'var(--storefront-palette-text, var(--storefront-text))',
} as CSSProperties;
